import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import type { AnalyticsQualityRow } from '@/api/client'
import { statusOf } from '@/lib/status'
import { DataTable, EmptyState, TonePill, type Column } from '@/ui'
import { fmtNum, fmtPct, UNPAIRED_WARN } from './format'
import { SortLabel, sortRows, useTableSort } from './sort'
import { Section } from './Section'

type QualitySort = 'app' | 'platform' | 'version' | 'events' | 'enters' | 'pair' | 'unpaired' | 'dupes' | 'dropped'
const QUALITY_TEXT: readonly QualitySort[] = ['app', 'platform', 'version']

function qualityValue(r: AnalyticsQualityRow, key: QualitySort): number | string {
  switch (key) {
    case 'app':
      return r.app_id
    case 'platform':
      return r.platform
    case 'version':
      return r.app_version
    case 'events':
      return r.events
    case 'enters':
      return r.enters
    case 'pair':
      return r.pair_rate
    case 'unpaired':
      return r.unpaired_enters
    case 'dupes':
      return r.duplicate_events
    case 'dropped':
      return r.discarded_total
  }
}

const rowKey = (r: AnalyticsQualityRow) => `${r.app_id}/${r.platform}/${r.app_version}`

/** Pair rate, duplicate rate and dropped events per app version. The table
 *  exists so a broken release announces itself instead of quietly corrupting
 *  three months of dwell. */
export function QualityCard({ rows, showApp = false }: { rows: AnalyticsQualityRow[]; showApp?: boolean }) {
  // Opens in the order the server sent, so the first look is unchanged; the
  // columns worth hunting through (pair rate, dropped) are one click away.
  const { sort, pick } = useTableSort<QualitySort>({ key: 'app', dir: 'asc' }, QUALITY_TEXT)
  const sorted = useMemo(() => sortRows(rows, sort, qualityValue, rowKey), [rows, sort])
  const head = (k: QualitySort, label: string, title?: string) => (
    <SortLabel k={k} sort={sort} onPick={pick} title={title}>
      {label}
    </SortLabel>
  )

  const columns: Column<AnalyticsQualityRow>[] = [
    ...(showApp
      ? [
          {
            key: 'app',
            header: head('app', '앱'),
            card: 'title' as const,
            cell: (r: AnalyticsQualityRow) => (
              <Link className="font-mono text-meta hover:text-accent hover:underline" to={`/analytics/${encodeURIComponent(r.app_id)}`}>
                {r.app_id}
              </Link>
            ),
          },
        ]
      : []),
    {
      key: 'platform',
      header: head('platform', '플랫폼'),
      card: showApp ? 'sub' : 'title',
      cell: (r) => r.platform || '—',
    },
    {
      key: 'version',
      header: head('version', '버전'),
      className: 'font-medium',
      card: showApp ? 'line' : 'meta',
      cell: (r) => r.app_version || '—',
    },
    { key: 'events', header: head('events', '이벤트'), align: 'right', className: 'tabular-nums', card: 'hidden', cell: (r) => fmtNum(r.events) },
    {
      // the phone's line for the volume; the pair rate below says if it is sound
      key: 'volume',
      header: '이벤트 · 진입/종료',
      cardOnly: true,
      cell: (r) => (
        <span className="tabular-nums">
          {fmtNum(r.events)} <span className="text-muted-foreground">·</span> {fmtNum(r.enters)} / {fmtNum(r.exits)}
        </span>
      ),
    },
    {
      key: 'enters',
      header: head('enters', '진입 / 종료', '화면 진입 수 / 화면 종료 수'),
      align: 'right',
      className: 'tabular-nums',
      card: 'hidden',
      cell: (r) => `${fmtNum(r.enters)} / ${fmtNum(r.exits)}`,
    },
    {
      key: 'pair',
      header: head('pair', '짝 비율', '종료가 짝으로 붙은 진입의 비율'),
      align: 'right',
      className: 'tabular-nums',
      card: 'line',
      cell: (r) => {
        const warn = r.enters > 0 && r.unpaired_enter_rate >= UNPAIRED_WARN
        if (r.enters === 0) return <span className="text-muted-foreground">{fmtPct(r.pair_rate, 0)}</span>
        const def = statusOf('quality', warn ? 'warn' : 'ok')
        return <TonePill tone={def.tone} label={fmtPct(r.pair_rate, 0)} className="tabular-nums" />
      },
    },
    { key: 'unpaired', header: head('unpaired', '짝 없음'), align: 'right', className: 'tabular-nums', card: 'hidden', cell: (r) => fmtNum(r.unpaired_enters) },
    {
      key: 'dupes',
      header: head('dupes', '중복'),
      align: 'right',
      className: 'tabular-nums',
      card: 'hidden',
      cell: (r) => (r.duplicate_events > 0 ? `${fmtNum(r.duplicate_events)} · ${fmtPct(r.duplicate_rate, 1)}` : '0'),
    },
    { key: 'dropped', header: head('dropped', '버려짐'), align: 'right', className: 'tabular-nums', card: 'hidden', cell: (r) => fmtNum(r.discarded_total) },
    {
      key: 'losses',
      header: '중복 · 버려짐',
      cardOnly: true,
      cardHide: (r) => r.duplicate_events === 0 && r.discarded_total === 0,
      cell: (r) => (
        <span className="tabular-nums">
          {fmtNum(r.duplicate_events)} <span className="text-muted-foreground">·</span> {fmtNum(r.discarded_total)}
        </span>
      ),
    },
  ]

  return (
    <Section title="데이터 품질" aside="앱 버전별 짝 비율, 중복 비율, 버려진 이벤트">
      <DataTable
        label="데이터 품질"
        rows={sorted}
        rowKey={rowKey}
        columns={columns}
        empty={<EmptyState title="이 기간에 이벤트가 없다" />}
      />
    </Section>
  )
}
